import http from '@/services/http';

const initialState = {
  // The current account details pulled from monolith.
  rawAccountDetails: {},
  // The current account revenue overrides if any.
  revenueOverrides: null,
  // Notification content that can display on the account details.
  // Displays information from previously executed action.
  previousActionNotification: {},
};

/**
 * Getter for company name.
 */
function companyName(state) {
  return state.rawAccountDetails !== {} ? state.rawAccountDetails.company : '';
}

/**
 * Getter for parentId. Will return null if the account is not a child.
 */
function parentId(state) {
  return state.rawAccountDetails.parent
    ? state.rawAccountDetails.parent.account_id
    : null;
}

/**
 * Getter for the billing engine version.
 */
function billingEngine(state) {
  return state.rawAccountDetails.billing_engine;
}

export const getters = { companyName, parentId, billingEngine };

/**
 * Get detail information for account id.
 * @param state { Object } state for the account vuex context
 * @param commit { Object } commit for the account vuex context
 * @param accountId { Number } the account id to get details for
 * @return { Object } The raw account details
 */
async function getAccountDetails({ state, commit }, accountId) {
  if (Object.keys(state.rawAccountDetails).length !== 0) {
    return state.rawAccountDetails;
  }
  const response = await http.getAccountDetails(accountId);
  if (response.data.status_code === 404) {
    throw new Error(`Account ${accountId} not found.`);
  }
  if (response.data.status_code !== 200) {
    throw new Error(response.data.status_message || 'An error has occurred');
  }
  commit('setAccountDetails', response.payload.data);
  return state.rawAccountDetails;
}

/**
 * Get revenue overrides for account id.
 * @param state { Object } state for the account vuex context
 * @param commit { Object } commit for the account vuex context
 * @param accountId { Number } the account id to get overrides
 * @return { Object } The raw revenue overrides.
 */
async function getRevenueOverrides({ state, commit }, accountId) {
  if (state.revenueOverrides !== null) {
    return state.revenueOverrides;
  }
  const response = await http.getRevenueOverride(accountId);
  if (response.data.status_code !== 200) {
    throw new Error(response.data.status_message || 'An error has occurred');
  }
  commit('setRevenueOverrides', response.payload.data);
  return state.revenueOverrides;
}

/**
 * Updates account.text_credits.
 * @param state { Object } state for the account vuex context
 * @param commit { Object } commit for the account vuex context
 * @param payload { Object } payload containing account_id and text_credits
 * @return {Promise<void>}
 */
async function updateTextCredits({ state, commit }, payload) {
  const { rawAccountDetails } = state;
  const response = await http.updateAccountTextCredits(payload);
  if (response.data.status_code !== 200) {
    throw new Error(response.data.status_message || 'An error has occurred');
  }
  const newDetails = { ...rawAccountDetails, text_credits: payload.text_credits };
  commit('setAccountDetails', newDetails);
  return state.rawAccountDetails;
}

/**
 * Reactivates the account. That will reset is_deactivated to False and deactivated_at to null.
 * @param state { Object } state for the account vuex context
 * @param commit { Object } commit for the account vuex context
 * @param payload { Object } payload containing account_id
 * @return {Promise<void>}
 */
async function reactivateAccount({ state, commit }, payload) {
  const response = await http.reactivateAccount(payload);
  if (response.data.status_code !== 200) {
    throw new Error(response.data.status_message || 'An error has occurred');
  }
  await getAccountDetails({ state, commit }, payload.account_id);
  return state.rawAccountDetails;
}

/**
 * Resets all account state
 */
function resetAccountState({ commit }) {
  commit('setAccountDetails', {});
  commit('setRevenueOverrides', null);
}

export const actions = {
  getAccountDetails, resetAccountState, updateTextCredits, reactivateAccount, getRevenueOverrides,
};

export const mutations = {
  setAccountDetails(state, details) {
    state.rawAccountDetails = details;
  },
  setRevenueOverrides(state, overrides) {
    state.revenueOverrides = overrides;
  },
  updateNotification(state, notification) {
    state.previousActionNotification = {
      message: notification.message,
      messageStyle: notification.messageStyle || 'success',
      buttonTxt: notification.buttonTxt,
      buttonLnk: notification.buttonLnk,
    };
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  actions,
  mutations,
};
